import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import type {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import SectionHeader from '@/components/Common/SectionHeader';
import {
    borderRadius,
    borderWidth,
    colors,
    contentWidth,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    shadows,
    spacing
} from '@/styles/tokens';

const checkIcon = cdnImage('web3/Web3-Compare-Check.svg');
const warningIcon = cdnImage('web3/Web3-Compare-Warning.svg');
const crossIcon = cdnImage('web3/Web3-Compare-Cross.svg');
const starIcon = cdnImage('web3/Web3-Compare-Star.svg');

export type CompareIconType = 'check' | 'warning' | 'cross' | 'star' | 'none';

export interface ComparisonCell {
    type: CompareIconType;
    text: string;
}

export interface ComparisonRow {
    criteria: string;
    debut: ComparisonCell;
    generic: ComparisonCell;
    offshore: ComparisonCell;
    inhouse: ComparisonCell;
}

export interface ComparisonTableProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    col1Label?: string;
    col3Label?: string;
    col4Label?: string;
    col5Label?: string;
    rows: ComparisonRow[];
    // Optional highlighted note(s) rendered below the table (e.g. SaaS payback note).
    footerNotes?: string[];
}

const iconSrcMap: Record<Exclude<CompareIconType, 'none'>, StaticImageData> = {
    check: checkIcon,
    warning: warningIcon,
    cross: crossIcon,
    star: starIcon
};

// pt reserves room for the floating badge — overflowX clips vertical overflow too,
// so the chip would be cut off without this top padding.
const scrollWrapperSx = {overflowX: 'auto', pt: '16px'} as const;

const tableSx = {
    minWidth: contentWidth.tableMin,
    bgcolor: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    boxShadow: shadows.sm,
    overflow: 'hidden'
} as const;
const rowGridSx = {
    display: 'grid',
    gridTemplateColumns: '200px 250px 1fr 1fr 1fr'
} as const;

const headerRowSx = {
    ...rowGridSx,
    bgcolor: colors.background.surfaceAlt
} as const;
const headerCellBaseSx = {
    px: {xs: spacing[12], md: spacing[24]},
    py: {xs: spacing[12], md: spacing[16]},
    borderBottom: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'start',
    justifyContent: 'end'
} as const;

const headerDebutCellSx = {
    ...headerCellBaseSx,
    bgcolor: colors.background.tag,
    borderTopLeftRadius: borderRadius.xl,
    borderTopRightRadius: borderRadius.xl
} as const;

const headerLabelSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.muted,
    lineHeight: 'normal'
} as const;

const headerDebutLabelSx = {
    ...headerLabelSx,
    color: colors.primary.ctaMid
} as const;
const tableWrapSx = {mt: '18px'} as const;
const tableInnerSx = {position: 'relative' as const, minWidth: contentWidth.tableMin, width: '100%'} as const;
const badgeFloatSx = {
    position: 'absolute' as const,
    top: '-14px',
    left: '325px',
    transform: 'translateX(-50%)',
    zIndex: 2,
    whiteSpace: 'nowrap' as const
} as const;

const badgeTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    bgcolor: colors.primary.main,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.inverse,
    lineHeight: lineHeight[20]
} as const;

// Highlighted note box below the table — light-blue fill with a left accent border.
const footerNoteSx = {
    mt: 4,
    p: 4,
    bgcolor: colors.background.tag,
    borderLeft: `3px solid ${colors.primary.main}`,
    borderRadius: borderRadius.md
} as const;

const footerNoteTextSx = {
    fontSize: fontSize[18]
} as const;

const bodyRowSx = {
    ...rowGridSx,
    borderTop: `${borderWidth.thin} solid ${colors.neutral.borderLight}`
} as const;

const criteriaCellSx = {
    px: {xs: spacing[12], md: spacing[24]},
    py: {xs: spacing[12], md: spacing[20]},
    display: 'flex',
    alignItems: 'center'
} as const;

const criteriaTextSx = {
    fontSize: fontSize[14]
} as const;

const debutCellBaseSx = {
    bgcolor: colors.background.tag,
    px: {xs: spacing[12], md: spacing[24]},
    py: spacing[12],
    display: 'flex',
    alignItems: 'center'
} as const;

const debutCellLastSx = {
    ...debutCellBaseSx,
    borderBottomLeftRadius: borderRadius.xl,
    borderBottomRightRadius: borderRadius.xl
} as const;

const otherCellSx = {
    px: {xs: spacing[12], md: spacing[24]},
    py: {xs: spacing[12], md: spacing[20]},
    display: 'flex',
    alignItems: 'center'
} as const;

const cellInnerSx = {display: 'flex', alignItems: 'center', gap: spacing[8]} as const;

const debutTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20]
} as const;

const starTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    color: colors.primary.link
} as const;

const otherTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular
} as const;

const iconAltMap: Record<Exclude<CompareIconType, 'none'>, string> = {
    check: 'Included',
    warning: 'Partial',
    cross: 'Not included',
    star: 'Best in class'
};

function CellIcon({type}: {type: Exclude<CompareIconType, 'none'>}) {
    const isWarning = type === 'warning';
    return (
        <Box sx={{width: isWarning ? 18 : 17, height: isWarning ? 16 : 17, position: 'relative', flexShrink: 0}}>
            <DisplayImage src={iconSrcMap[type]} alt={iconAltMap[type]} fill />
        </Box>
    );
}

function CellContent({cell, isDebut}: {cell: ComparisonCell; isDebut: boolean}) {
    const textSx = cell.type === 'star' ? starTextSx : isDebut ? debutTextSx : otherTextSx;
    return (
        <Box sx={cellInnerSx}>
            {cell.type !== 'none' && <CellIcon type={cell.type} />}
            <Typography sx={textSx}>{cell.text}</Typography>
        </Box>
    );
}

const OTHER_COLUMNS: ReadonlyArray<{labelProp: keyof ComparisonTableProps; cellKey: keyof ComparisonRow}> = [
    {labelProp: 'col3Label', cellKey: 'generic'},
    {labelProp: 'col4Label', cellKey: 'offshore'},
    {labelProp: 'col5Label', cellKey: 'inhouse'}
];

const ComparisonTable = (props: ComparisonTableProps) => {
    const {
        eyebrow,
        heading,
        subheading,
        col1Label = 'Evaluation Criteria',
        col3Label = 'Generic Dev Shop',
        col4Label = 'Offshore Freelance',
        col5Label = 'In-House Build',
        rows,
        footerNotes
    } = props;
    const labels = {col1Label, col3Label, col4Label, col5Label};
    const lastIdx = rows.length - 1;

    return (
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />

            <Box sx={tableWrapSx}>
                <Box sx={scrollWrapperSx}>
                    <Box sx={tableInnerSx}>
                        <Box sx={badgeFloatSx}>
                            <Typography component="span" className="font-headers" sx={badgeTextSx}>
                                ★ Recommended
                            </Typography>
                        </Box>
                        <Box sx={tableSx}>
                            <Box sx={headerRowSx}>
                                <Box sx={headerCellBaseSx}>
                                    <Typography className="font-headers" sx={headerLabelSx}>
                                        {col1Label}
                                    </Typography>
                                </Box>
                                <Box sx={headerDebutCellSx}>
                                    <Typography className="font-headers" sx={headerDebutLabelSx}>
                                        Debut Infotech
                                    </Typography>
                                </Box>
                                {OTHER_COLUMNS.map((col) => (
                                    <Box key={col.cellKey} sx={headerCellBaseSx}>
                                        <Typography className="font-headers" sx={headerLabelSx}>
                                            {labels[col.labelProp as keyof typeof labels]}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* Body rows */}
                            {rows.map((row, idx) => (
                                <Box key={row.criteria} sx={bodyRowSx}>
                                    <Box sx={criteriaCellSx}>
                                        <Typography className="font-headers" sx={criteriaTextSx}>
                                            {row.criteria}
                                        </Typography>
                                    </Box>
                                    <Box sx={idx === lastIdx ? debutCellLastSx : debutCellBaseSx}>
                                        <CellContent cell={row.debut} isDebut={true} />
                                    </Box>
                                    {OTHER_COLUMNS.map((col) => (
                                        <Box key={col.cellKey} sx={otherCellSx}>
                                            <CellContent cell={row[col.cellKey] as ComparisonCell} isDebut={false} />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Optional highlighted notes below the table (only rendered when passed) */}
            {footerNotes?.map((note, index) => (
                <Box key={index} sx={footerNoteSx}>
                    <Typography component="p" className="font-headers" sx={footerNoteTextSx}>
                        {note}
                    </Typography>
                </Box>
            ))}
        </MUIContainer>
    );
};

export default memo(ComparisonTable);
